import * as Yup from "yup";
import i18n from "../i18n";

export function getFormSchema(isForeigner) {
  const PHONE_NUMBER_LENGTH = 10;
  const PHONE_NUMBER_ERROR_MESSAGE = `${i18n.t("form:phoneLength", {
    length: PHONE_NUMBER_LENGTH,
  })} ${i18n.t("form:digit")}`;
  const thaiFormSchema = Yup.object({
    title: Yup.string().required(i18n.t("form:title")),
    firstNameTh: Yup.string()
      .required(i18n.t("form:firstNameThRequired"))
      .test("is-thai", i18n.t("form:firstNameThThaiOnly"), (value) =>
        /^[ก-์\s]+$/.test(value)
      ),
    middleNameTh: Yup.string().optional(),
    lastNameTh: Yup.string()
      .required(i18n.t("form:lastNameThRequired"))
      .test("is-thai", i18n.t("form:lastNameThThaiOnly"), (value) =>
        /^[ก-์\s]+$/.test(value)
      ),
    firstNameEn: Yup.string()
      .optional()
      .test(
        "is-english",
        i18n.t("form:firstNameEnEnglishOnly"),
        (value) => !value || /^[a-zA-Z\s]+$/.test(value)
      ),
    middleNameEn: Yup.string().optional(),
    lastNameEn: Yup.string()
      .optional()
      .test(
        "is-english",
        i18n.t("form:lastNameEnEnglishOnly"),
        (value) => !value || /^[a-zA-Z\s]+$/.test(value)
      ),
    dob: Yup.string().optional(),
    idCard: Yup.string().optional(),
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10}$/, i18n.t("form:phoneInvalid"))
      .required(i18n.t("form:phoneRequired"))
      .min(PHONE_NUMBER_LENGTH, PHONE_NUMBER_ERROR_MESSAGE)
      .max(PHONE_NUMBER_LENGTH, PHONE_NUMBER_ERROR_MESSAGE),
    email: Yup.string()
      .email(i18n.t("form:emailInvalid"))
      .required(i18n.t("form:emailRequired")),
    status: Yup.string().optional(),
    district: Yup.string().required(i18n.t("form:districtRequired")),
    province: Yup.string().required(i18n.t("form:provinceRequired")),
    address: Yup.string().required(i18n.t("form:addressRequired")),
    subDistrict: Yup.string().required(i18n.t("form:subDistrictRequired")),
    postalCode: Yup.string().required(i18n.t("form:postalCodeRequired")),
  });

  const foreignFormSchema = Yup.object({
    title: Yup.string().required(i18n.t("form:title")),
    firstNameEn: Yup.string()
      .required(i18n.t("form:firstNameEnRequired"))
      .test(
        "is-firstname-english",
        i18n.t("form:firstNameEnEnglishOnly"),
        (value) => /^[a-zA-Z\s]+$/.test(value)
      ),
    middleNameEn: Yup.string()
      .optional()
      .test(
        "is-middle-english",
        i18n.t("form:middleNameEnEnglishOnly"),
        (value) => !value || /^[a-zA-Z\s]+$/.test(value)
      ),
    lastNameEn: Yup.string()
      .required(i18n.t("form:lastNameEnRequired"))
      .test(
        "is-lastname-english",
        i18n.t("form:lastNameEnEnglishOnly"),
        (value) => /^[a-zA-Z\s]+$/.test(value)
      ),
    dob: Yup.date().required(i18n.t("form:dobRequired")),
    nationality: Yup.string().required(i18n.t("form:nationalityRequired")),
    idPassport: Yup.string().required(i18n.t("form:passportRequired")),
    phoneNumber: Yup.string()
      .required(i18n.t("form:phoneRequired"))
      .min(PHONE_NUMBER_LENGTH, PHONE_NUMBER_ERROR_MESSAGE)
      .max(PHONE_NUMBER_LENGTH, PHONE_NUMBER_ERROR_MESSAGE),
    email: Yup.string()
      .email(i18n.t("form:emailInvalid"))
      .required(i18n.t("form:emailRequired")),
    status: Yup.string().required(i18n.t("form:statusRequired")),
    district: Yup.string().required(i18n.t("form:districtRequired")),
    province: Yup.string().required(i18n.t("form:provinceRequired")),
    address: Yup.string().required(i18n.t("form:addressRequired")),
    subDistrict: Yup.string().required(i18n.t("form:subDistrictRequired")),
    postalCode: Yup.string().required(i18n.t("form:postalCodeRequired")),
  });

  // Always return Thai schema — registration now defaults to Thai nationality
  return thaiFormSchema;
}

export function getFormInitialValues(isForeigner, { idCard, dob }) {
  const initialValuesForThai = {
    title: "",
    firstNameTh: "",
    middleNameTh: "-",
    lastNameTh: "",
    firstNameEn: "",
    middleNameEn: "-",
    lastNameEn: "",
    dob: "2000-01-01",
    idCard: idCard || String(Date.now()).slice(-13).padStart(13, "0"),
    phoneNumber: "",
    email: "",
    status: "single",
    district: "",
    province: "",
    address: "",
    subDistrict: "",
    postalCode: "",
  };

  const initialValuesForForeign = {
    title: "",
    firstNameEn: "",
    middleNameEn: "",
    lastNameTh: "",
    dob,
    nationality: "",
    idPassport: idCard,
    phoneNumber: "",
    email: "",
    status: "",
    district: "",
    province: "",
    address: "",
    subDistrict: "",
    postalCode: "",
  };

  return Boolean(isForeigner) ? initialValuesForForeign : initialValuesForThai;
}

export function convertTitleToGender(title) {
  switch (title) {
    case "นาย":
      return "male";
    case "นาง":
      return "female";
    case "นางสาว":
      return "female";
    default:
      throw new Error("Invalid title");
  }
}

export function mapPayloadToCreateCustomerForAunjaiAPI({
  formValues,
  isForeigner,
  targetLineUUID,
}) {
  return {
    title: formValues.title,
    first_name_th: formValues.firstNameTh,
    first_name_en: formValues.firstNameEn || "-",
    last_name_th: formValues.lastNameTh,
    last_name_en: formValues.lastNameEn || "-",
    middle_name_th: formValues.middleNameTh || "-",
    middle_name_en: formValues.middleNameEn || "-",
    id_card: formValues.idCard,
    phone: formValues.phoneNumber,
    email: formValues.email,
    date_of_birth: formValues.dob,
    gender: convertTitleToGender(formValues.title),
    status: formValues.status || "single",
    address: formValues.address,
    province: formValues.provinceName,
    district: formValues.districtName,
    sub_district: formValues.subDistrictName,
    zip: JSON.stringify(formValues.postalCode),
    is_foreigner: 0,
    is_allow_loan: true,
    loan_request: 5, // TODO : ต้องใช้การคำนวณจริง
    targetLineUUID,
    nationality: formValues.nationality,
  };
}
